/**
 * mlist-api JWT auth for interim Admin Tools bridge panels only.
 * Does not replace legacy Django hoodie session login.
 *
 * Token endpoint: POST {apiBase}auth/token/ with { email, password }.
 * Storage keys match existing mainApi convention in services/api.js.
 */

import {
  getMlistApiConfig,
  isMlistApiConfigured,
  isMlistApiValid,
  MLIST_API_TOKEN_URL,
} from "./mlistConfig";

export const MLIST_ACCESS_TOKEN_KEY = "accessToken";
export const MLIST_REFRESH_TOKEN_KEY = "refreshToken";

export function getMlistAccessToken() {
  try {
    return localStorage.getItem(MLIST_ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getMlistRefreshToken() {
  try {
    return localStorage.getItem(MLIST_REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setMlistTokens(access, refresh) {
  localStorage.setItem(MLIST_ACCESS_TOKEN_KEY, access);
  if (refresh) {
    localStorage.setItem(MLIST_REFRESH_TOKEN_KEY, refresh);
  }
}

export function clearMlistApiTokens() {
  localStorage.removeItem(MLIST_ACCESS_TOKEN_KEY);
  localStorage.removeItem(MLIST_REFRESH_TOKEN_KEY);
}

export function hasMlistApiAuth() {
  return Boolean(getMlistAccessToken());
}

/**
 * Obtain JWT from mlist-api (staff login).
 * @param {{ email: string, password: string }} credentials
 * @returns {Promise<{ access: string, refresh: string }>}
 */
export async function loginMlistApi({ email, password }) {
  if (!isMlistApiConfigured()) {
    const err = new Error(
      "REACT_APP_MLIST_API_BASE_URL is not set; cannot log in to mlist-api"
    );
    err.code = "mlist_api_not_configured";
    throw err;
  }

  const config = getMlistApiConfig();
  if (!config.valid || !config.tokenUrl) {
    const err = new Error(
      config.errorMessage || "Invalid MList API base URL"
    );
    err.code = "mlist_api_invalid_url";
    throw err;
  }

  const tokenUrl = config.tokenUrl || MLIST_API_TOKEN_URL;
  if (!tokenUrl || !isMlistApiValid()) {
    const err = new Error("Invalid MList API base URL");
    err.code = "mlist_api_invalid_url";
    throw err;
  }

  let response;
  try {
    response = await fetch(tokenUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "omit",
      body: JSON.stringify({
        email: String(email || "").trim(),
        password,
      }),
    });
  } catch (networkErr) {
    const err = new Error(
      networkErr?.message === "Failed to fetch"
        ? `Network error calling ${tokenUrl}. Check REACT_APP_MLIST_API_BASE_URL, CORS, and API availability.`
        : networkErr?.message || "Network error"
    );
    err.code = "mlist_api_network_error";
    err.cause = networkErr;
    throw err;
  }

  const contentType = response.headers.get("content-type");
  const data =
    contentType && contentType.includes("application/json")
      ? await response.json()
      : null;

  if (!response.ok) {
    const err = new Error(
      data?.detail || data?.message || `HTTP ${response.status}`
    );
    err.status = response.status;
    err.data = data;
    throw err;
  }

  if (!data?.access) {
    throw new Error("Token response missing access token");
  }

  setMlistTokens(data.access, data.refresh);
  return { access: data.access, refresh: data.refresh };
}
